import { useCallback } from 'react';
import type React from 'react';

import { Button } from '@change/ds-core-ui';
import { TrashIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';

import { useLoginRedirectUrl } from '#src/app/shared/hooks/auth';
import { useLoginState } from '#src/app/shared/hooks/session';

import { CommentDeleteModal } from './components/CommentDeleteModal';
import type { PartialComment } from './hooks/types';

type Props = {
	comment: PartialComment;
	onDeleted?: () => void;
	color?: string;
	size?: 'x-small' | 'small' | 'medium' | 'large' | 'x-large';
	className?: string;
};

export const CommentDelete: React.FC<Props> = ({ comment, onDeleted, color, size, className }) => {
	const { translate } = useI18n();
	const loginState = useLoginState();
	const navigate = useNavigate();
	const loginPath = useLoginRedirectUrl();
	const track = useTracking();

	const a11yLabel = translate('corgi.petition.details.comments.actions.delete_a11y', {
		author: comment.user.displayName,
		date: comment.createdAtLocalized,
	});

	const handleDeleted = useCallback(() => {
		void track('comment_delete', {
			likes: comment.likes,
			role: comment.role.toLowerCase(),
			comment_id: `comment_${comment.id}`,
			parent_id: comment.parentId || undefined,
		});
		onDeleted?.();
	}, [track, comment, onDeleted]);

	const isAuthenticated = loginState === 'AUTHENTICATED';

	const deleteButton = (
		<Button
			variant="tertiary"
			aria-label={a11yLabel}
			data-testid="comment-delete"
			data-qa="comment-delete"
			className={cn(
				'hover:text-icon-error p-0 hover:bg-transparent! active:bg-transparent! md:hover:bg-transparent!',
				color,
				className,
			)}
			onClick={isAuthenticated ? undefined : () => navigate(loginPath)}
		>
			<TrashIcon className="flex items-center" size={size} />
		</Button>
	);

	return isAuthenticated ? (
		<CommentDeleteModal comment={comment} onDeleted={handleDeleted} trigger={deleteButton} />
	) : (
		deleteButton
	);
};
